import React, { useContext, useEffect, useState } from "react"
import { Alert, Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"

import LlmInstrContext from "./LlmInstrContext"
import { STRATEGY_LABELS } from "./constants"

/**
 * The reply, in full, exactly as the LLM is given it.
 *
 * Writing an instruction is quoting work -- "when the reply says PLEASE CONFIRM", "the code
 * appears as PU#1234" -- so the wording has to be readable and selectable on the page. A
 * snippet in a grid cell cannot serve that.
 *
 * Fetched when opened rather than shipped with the listings: these are whole quoted threads,
 * routinely tens of KB, and the two grids together list twenty of them.
 */
function MessageModal({ reply, onClose }) {
  const { fetchMessage } = useContext(LlmInstrContext)
  const [state, setState] = useState({ loading: true })

  useEffect(() => {
    if (!reply) return undefined
    let live = true
    setState({ loading: true })
    fetchMessage(reply.mail_tracker, reply.message_id)
      .then(data => live && setState({ loading: false, data }))
      .catch(err => live && setState({ loading: false, error: err.message }))
    // Cancels the state write if the modal closes (or the row changes) mid-flight.
    return () => { live = false }
  }, [reply, fetchMessage])

  if (!reply) return null
  const body = state.data?.response_mail

  return (
    <Modal isOpen toggle={onClose} size="lg" scrollable>
      <ModalHeader toggle={onClose}>
        <span className="small">
          Facility reply
          {reply.response_rcvd_on ? ` · ${reply.response_rcvd_on.replace("T", " ").slice(0, 16)}` : ""}
        </span>
      </ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap gap-2 align-items-center mb-2 small text-muted">
          <Badge color="light" className="text-dark border fw-normal">
            QD Ref {reply.mail_tracker}
          </Badge>
          <Badge color="light" className="text-dark border fw-normal">
            message {reply.message_id}
          </Badge>
          {reply.confirmation_strategy && (
            <Badge color="light" className="text-dark border fw-normal">
              {STRATEGY_LABELS[reply.confirmation_strategy] || reply.confirmation_strategy}
            </Badge>
          )}
        </div>

        {state.loading && (
          <div className="text-muted small"><Spinner size="sm" className="me-1" /> loading…</div>
        )}
        {state.error && <Alert color="danger" className="small py-2">{state.error}</Alert>}
        {!state.loading && !state.error && (
          <pre
            className="bg-light border rounded p-2 mb-0 small"
            style={{ whiteSpace: "pre-wrap", maxHeight: "60vh", overflow: "auto" }}
          >
            {body || "No stored body for this reply — it was processed before the body was retained."}
          </pre>
        )}
        <div className="text-muted mt-2" style={{ fontSize: "0.72rem" }}>
          This is the exact text the LLMs receive — the quoted thread as the facility sent it.
          Select from it when wording an instruction about replies that look like this one.
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" size="sm" onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}

export default MessageModal
